import { Loader2, Send, Trash2 } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { useT } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { TelegramBot } from "@/lib/types"

/** Telegram user ids, one per line or comma-separated; always positive numbers. */
function parseUserIds(raw: string): { ids: number[]; invalid: string[] } {
  const ids: number[] = []
  const invalid: string[] = []
  for (const part of raw.split(/[\s,]+/).filter(Boolean)) {
    if (/^\d+$/.test(part)) ids.push(Number(part))
    else invalid.push(part)
  }
  return { ids: [...new Set(ids)], invalid }
}

export function TelegramCard({ agentId }: { agentId: string }) {
  const t = useT()
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [bot, setBot] = React.useState<TelegramBot | null>(null)
  const [token, setToken] = React.useState("")
  const [userIdsText, setUserIdsText] = React.useState("")
  const [enabled, setEnabled] = React.useState(true)

  React.useEffect(() => {
    api
      .getAgentTelegram(agentId)
      .then((loaded) => {
        setBot(loaded)
        if (loaded) {
          setUserIdsText(loaded.allowed_user_ids.join("\n"))
          setEnabled(loaded.enabled)
        }
      })
      .catch(() => toast.error(t("telegram.toasts.loadFailed")))
      .finally(() => setLoading(false))
  }, [agentId, t])

  const { ids, invalid } = parseUserIds(userIdsText)

  async function save() {
    if (invalid.length) {
      toast.error(t("telegram.invalidIds", { ids: invalid.join(", ") }))
      return
    }
    if (!bot && !token.trim()) {
      toast.error(t("telegram.toasts.missingToken"))
      return
    }
    setSaving(true)
    try {
      const payload: Record<string, unknown> = { allowed_user_ids: ids, enabled }
      if (token.trim()) payload.bot_token = token.trim()
      const saved = await api.saveAgentTelegram(agentId, payload)
      setBot(saved)
      setToken("")
      toast.success(t("telegram.toasts.connected", { username: saved.username }))
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("telegram.toasts.saveFailed"))
    } finally {
      setSaving(false)
    }
  }

  async function disconnect() {
    if (!confirm(t("telegram.disconnectConfirm"))) return
    try {
      await api.deleteAgentTelegram(agentId)
      setBot(null)
      setToken("")
      setUserIdsText("")
      toast.success(t("telegram.toasts.disconnected"))
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("common.disconnectFailed"))
    }
  }

  if (loading) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="size-4" />
          {t("telegram.title")}
        </CardTitle>
        <CardDescription>{t("telegram.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {bot ? (
          <p className="text-sm">
            {t("common.connectedAs")}{" "}
            <a href={bot.link} target="_blank" rel="noopener noreferrer" className="underline">
              @{bot.username}
            </a>{" "}
            <span className="text-muted-foreground">
              {t("telegram.tokenSuffix", { last4: bot.token_last4 })}
            </span>
          </p>
        ) : (
          <ol className="text-muted-foreground list-decimal pl-5 text-xs">
            <li>{t("telegram.steps.step1")}</li>
            <li>{t("telegram.steps.step2")}</li>
            <li>{t("telegram.steps.step3")}</li>
            <li>{t("telegram.steps.step4")}</li>
          </ol>
        )}

        {bot?.last_error && (
          <Alert variant="destructive">
            <AlertTitle>{t("telegram.errorTitle")}</AlertTitle>
            <AlertDescription>{bot.last_error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="tg-token">{t("telegram.tokenLabel")}</Label>
          <Input
            id="tg-token"
            type="password"
            value={token}
            onChange={(event) => setToken(event.target.value)}
            placeholder={bot ? t("telegram.tokenPlaceholderKeep") : "123456789:AA…"}
            autoComplete="off"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="tg-users">{t("telegram.userIdsLabel")}</Label>
          <Textarea
            id="tg-users"
            rows={3}
            value={userIdsText}
            onChange={(event) => setUserIdsText(event.target.value)}
            placeholder={"777000111\n423456789"}
          />
          <p className="text-muted-foreground text-xs">{t("telegram.userIdsHint")}</p>
          {invalid.length > 0 && (
            <p className="text-destructive text-xs">
              {t("telegram.invalidIds", { ids: invalid.join(", ") })}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Switch id="tg-enabled" checked={enabled} onCheckedChange={setEnabled} />
          <Label htmlFor="tg-enabled">{t("common.enabled")}</Label>
        </div>

        <div className="flex gap-2">
          <Button type="button" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : null}
            {bot ? t("telegram.submit.save") : t("telegram.submit.connect")}
          </Button>
          {bot && (
            <Button type="button" variant="ghost" className="text-destructive" onClick={disconnect}>
              <Trash2 className="size-4" />
              {t("common.disconnect")}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
